import { getCategoryColor, getCategoryLabel } from "./aiMappingColors";
import { groupDetectionsByCategory } from "./aiMappingDetections";
import { AiMappingResultsLike } from "./aiMappingTypes";

export type AssignmentSource = {
  id: string;
  kind: "ai_count" | "ai_measured_area" | "map_measurement";
  label: string;
  detail: string;
  color: string;
  count?: number;
  areaSqft?: number;
  shapeCount?: number;
  stallLinearFeet?: number;
  areaNames?: string[];
  areaIds?: string[];
  categories?: string[];
  mapMeasurementId?: string;
  assignedProductId?: string | null;
};

export type AiAssignmentSource = AssignmentSource;

const colorNames: Record<string, string> = {
  "#00d4ff": "Blue",
  "#51cf66": "Green",
  "#69db7c": "Green",
  "#ff6b6b": "Red",
  "#ffa94d": "Orange",
  "#ffd43b": "Yellow",
  "#845ef7": "Purple",
  "#339af0": "Blue",
};

const colorLabel = (color: string) => colorNames[color.toLowerCase()] ?? "Colored";

export function buildAiAssignmentSources(results: AiMappingResultsLike[]): AssignmentSource[] {
  const counts = new Map<
    string,
    { count: number; color: string; areaNames: Set<string>; areaIds: Set<string> }
  >();
  const measuredAreas = new Map<
    string,
    {
      sqft: number;
      shapeCount: number;
      areaNames: Set<string>;
      areaIds: Set<string>;
      categories: Set<string>;
    }
  >();

  results.forEach((result) => {
    const areaName = result.area.name || "Untitled AI area";
    groupDetectionsByCategory(result.detections).forEach((group) => {
      const current = counts.get(group.category);
      if (current) {
        current.count += group.count;
        current.areaNames.add(areaName);
        current.areaIds.add(result.area.id);
      } else {
        counts.set(group.category, {
          count: group.count,
          color: group.color,
          areaNames: new Set([areaName]),
          areaIds: new Set([result.area.id]),
        });
      }
    });

    result.segmentations
      .filter((segmentation) => !segmentation.isDeleted)
      .forEach((segmentation) => {
        const color = getCategoryColor(segmentation.category, segmentation.color);
        const current = measuredAreas.get(color);
        if (current) {
          current.sqft += segmentation.areaSqft ?? 0;
          current.shapeCount += 1;
          current.areaNames.add(areaName);
          current.areaIds.add(result.area.id);
          current.categories.add(segmentation.category);
        } else {
          measuredAreas.set(color, {
            sqft: segmentation.areaSqft ?? 0,
            shapeCount: 1,
            areaNames: new Set([areaName]),
            areaIds: new Set([result.area.id]),
            categories: new Set([segmentation.category]),
          });
        }
      });
  });

  const countSources = Array.from(counts.entries()).map(([category, value]) => {
    const linearFeet =
      category === "single_stalls"
        ? value.count * 20
        : category === "double_stalls"
        ? value.count * 25
        : undefined;
    const areaNames = Array.from(value.areaNames).sort();
    return {
      id: `count:${category}`,
      kind: "ai_count" as const,
      label: getCategoryLabel(category),
      detail: linearFeet
        ? `${value.count.toLocaleString()} stalls · ${linearFeet.toLocaleString()} linear ft`
        : `${value.count.toLocaleString()} counted`,
      color: value.color,
      count: value.count,
      stallLinearFeet: linearFeet,
      areaNames,
      areaIds: Array.from(value.areaIds).sort(),
    };
  });

  const measuredAreaSources = Array.from(measuredAreas.entries()).map(([color, value]) => ({
    id: `measured-area:${color.toLowerCase()}`,
    kind: "ai_measured_area" as const,
    label: `${colorLabel(color)} measured areas`,
    detail: `${Math.round(value.sqft).toLocaleString()} ft² · ${value.shapeCount} ${
      value.shapeCount === 1 ? "area" : "areas"
    }`,
    color,
    areaSqft: value.sqft,
    shapeCount: value.shapeCount,
    areaNames: Array.from(value.areaNames).sort(),
    areaIds: Array.from(value.areaIds).sort(),
    categories: Array.from(value.categories).sort(),
  }));

  return [...countSources, ...measuredAreaSources].sort((a, b) => a.label.localeCompare(b.label));
}
